// ─────────────────────────────────────────────────────────────────
//  VisaTracking.jsx
//  Visa Filing  Approval Tracking Stages
//  Connected to: Profile.css, ProfileApplication.jsx, ProfileOverview.jsx
// ─────────────────────────────────────────────────────────────────

import React, { useEffect } from "react";
import ReactDOM from "react-dom";

// ─────────────────────────────────────────────────────────────────
//  useAOS Hook
// ─────────────────────────────────────────────────────────────────
function useAOS() {
  useEffect(() => {
    if (window.AOS) {
      window.AOS.init({
        duration: 650,
        easing: "ease-out-cubic",
        once: true,
        offset: 60,
      });
    }
    return () => {
      if (window.AOS) {
        window.AOS.refresh();
      }
    };
  }, []);
}

// ─────────────────────────────────────────────────────────────────
//  CONSTANTS — 12 Visa Filing Stages
// ─────────────────────────────────────────────────────────────────
export const TRACKING_STAGES = [
  {
    id: "ONB-01",
    title: "Client Onboarding",
    emoji: "🧾",
    details: [
      "Client details collected",
      "Passport copy received",
      "Travel purpose confirmed",
      "Service agreement / payment initiated",
    ],
  },
  {
    id: "DOC-02",
    title: "Document Collection",
    emoji: "📄",
    details: [
      "Required documents checklist shared",
      "Client submits documents",
      "Documents verified internally",
    ],
  },
  {
    id: "VER-03",
    title: "Document Verification & QC",
    emoji: "🔍",
    details: [
      "Cross-check passport validity",
      "Financial / supporting docs verified",
      "Corrections requested (if needed)",
    ],
  },
  {
    id: "APP-04",
    title: "Visa Application Form Filling",
    emoji: "📝",
    details: [
      "Application filled (online/offline depending on country)",
      "Draft shared with client (optional)",
      "Final submission-ready form",
    ],
  },
  {
    id: "FEE-05",
    title: "Fee Payment",
    emoji: "💳",
    details: [
      "Visa fee calculated",
      "Payment completed",
      "Receipt generated",
    ],
  },
  {
    id: "APT-06",
    title: "Appointment Scheduling",
    emoji: "📅",
    details: [
      "Biometrics / interview appointment booked",
      "Appointment confirmation shared",
    ],
  },
  {
    id: "BIO-07",
    title: "Biometrics Submission",
    emoji: "🧬",
    details: [
      "Client visits VAC (Visa Application Center)",
      "Fingerprints + photo submitted",
    ],
  },
  {
    id: "SUB-08",
    title: "Application Submission",
    emoji: "📤",
    details: [
      "File submitted to embassy / consulate",
      "Submission acknowledgment received",
    ],
  },
  {
    id: "PRC-09",
    title: "Embassy Processing",
    emoji: "🏛️",
    details: [
      "Under review",
      "Additional documents requested (if any)",
      "Background checks",
    ],
  },
  {
    id: "DEC-10",
    title: "Decision Made",
    emoji: "📬",
    details: [
      "Visa approved / rejected / on hold",
      "Decision updated in system",
    ],
  },
  {
    id: "DSP-11",
    title: "Passport Dispatch / Collection",
    emoji: "📦",
    details: [
      "Passport ready for pickup or courier",
      "Tracking number shared",
    ],
  },
  {
    id: "CLS-12",
    title: "Visa Delivered to Client",
    emoji: "✅",
    details: [
      "Passport received by client",
      "Case closed",
    ],
  },
];

// ─────────────────────────────────────────────────────────────────
//  HELPER — Determine current stage index from application data
// ─────────────────────────────────────────────────────────────────
export function getTrackingStageIndex(app) {
  if (!app) return 0;

  if (app.status === "completed") {
    if (app.decisionStatus === "approved") return 11; // CLS-12
    if (app.decisionStatus === "rejected") return 9;  // DEC-10
    if (app.decisionStatus === "on-hold")  return 9;  // DEC-10
    return 8; // PRC-09 — completed payment, awaiting decision
  }

  // Fresh pending application
  return 0; // ONB-01
}

// ─────────────────────────────────────────────────────────────────
//  DECISION CONFIG — maps decisionStatus to display label/color
// ─────────────────────────────────────────────────────────────────
export const DECISION_CONFIG = {
  approved: { label: "Approved",       color: "#059669", bg: "rgba(5,150,105,0.1)",  border: "rgba(5,150,105,0.25)"  },
  rejected: { label: "Rejected",       color: "#dc2626", bg: "rgba(220,38,38,0.1)",  border: "rgba(220,38,38,0.25)"  },
  "on-hold":{ label: "On Hold",        color: "#b45309", bg: "rgba(217,119,6,0.1)",  border: "rgba(217,119,6,0.25)"  },
  pending:  { label: "Decision Pending (DEC-10)", color: "#64748b", bg: "rgba(100,116,139,0.08)", border: "rgba(100,116,139,0.2)" },
};

export function getDecisionConfig(app) {
  if (!app) return null;
  if (app.decisionStatus) return DECISION_CONFIG[app.decisionStatus] ?? null;
  if (app.status === "completed") return DECISION_CONFIG.pending;
  return null;
}

// ─────────────────────────────────────────────────────────────────
//  COMPONENT — Tracking Modal
// ─────────────────────────────────────────────────────────────────
export function TrackingModal({ app, onClose }) {
  useAOS();
  const currentIndex = getTrackingStageIndex(app);
  const decisionCfg  = getDecisionConfig(app);
  const currentStage = TRACKING_STAGES[currentIndex];

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="VisaTrackOverlay" onClick={onClose} data-aos="fade-in">
      <div className="VisaTrackModal" onClick={(e) => e.stopPropagation()} data-aos="zoom-in">

        {/* ── Header ── */}
        <div className="VisaTrackHeader" data-aos="fade-up">
          <div className="VisaTrackHeaderInfo">
            <div className="VisaTrackHeaderTitle">Application Tracking</div>
            <div className="VisaTrackHeaderSub">
              {app?.countryName}
              {app?.visaTypeLabel ? ` · ${app.visaTypeLabel}` : ""}
            </div>
          </div>
          <div className="VisaTrackHeaderRight">
            <span className="VisaTrackAppId">{app?.applicationId || app?.humanId || app?.id}</span>
            <button className="VisaTrackClose" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* ── Current Stage Banner ── */}
        <div className="VisaTrackCurrentBanner" data-aos="fade-up" data-aos-delay={100}>
          <span className="VisaTrackCurrentEmoji">{currentStage.emoji}</span>
          <div>
            <div className="VisaTrackCurrentTitle">Current Stage: {currentStage.title}</div>
            <div className="VisaTrackCurrentId">{currentStage.id} — Step {currentIndex + 1} of {TRACKING_STAGES.length}</div>
          </div>
          {decisionCfg && (
            <span
              className="VisaTrackDecisionBadge"
              style={{ "--dcfg-color": decisionCfg.color, "--dcfg-bg": decisionCfg.bg, "--dcfg-border": decisionCfg.border }}
            >
              {decisionCfg.label}
            </span>
          )}
        </div>

        {/* ── Timeline ── */}
        <div className="VisaTrackTimeline">
          {TRACKING_STAGES.map((stage, idx) => {
            const isDone   = idx < currentIndex;
            const isActive = idx === currentIndex;
            const stateClass = isDone ? "VisaTrackStep--done" : isActive ? "VisaTrackStep--active" : "VisaTrackStep--upcoming";

            return (
              <div key={stage.id} className={`VisaTrackStep ${stateClass}`} data-aos="fade-up" data-aos-delay={50 * (idx + 1)}>

                {/* Left: icon + connector line */}
                <div className="VisaTrackStepLeft">
                  <div className="VisaTrackStepIconWrap">
                    {isDone
                      ? <span className="VisaTrackStepCheck">✓</span>
                      : <span className="VisaTrackStepEmoji">{stage.emoji}</span>
                    }
                  </div>
                  {idx < TRACKING_STAGES.length - 1 && (
                    <div className={`VisaTrackConnector${isDone ? " done" : isActive ? " active" : ""}`} />
                  )}
                </div>

                {/* Right: content */}
                <div className="VisaTrackStepContent">
                  <div className="VisaTrackStepRow">
                    <span className="VisaTrackStepTitle">{stage.title}</span>
                    <span className="VisaTrackStepId">{stage.id}</span>
                  </div>
                  {(isDone || isActive) && (
                    <ul className="VisaTrackStepDetails">
                      {stage.details.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  )}
                </div>

              </div>
            );
          })}
        </div>

      </div>
    </div>,
    document.body
  );
}
